package org.example.project.service

import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.serialization.json.encodeToJsonElement
import kotlinx.serialization.json.putJsonArray
import kotlinx.serialization.json.putJsonObject
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.enginetabular.loadWithTabularFilters
import org.example.project.utils.All
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

object YardApi {
    private const val EMPTY_FIELD = "~"
    private val standortLayoutCache = mutableMapOf<String, All>()

    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    suspend fun loadKontrolleDefinitionen(firmenId: String = ""): List<All> =
        decodeList(callScoped(firmenId, "kontrollen"))

    suspend fun loadYardSettings(firmenId: String = ""): All? =
        decodeOne(callScoped(firmenId, "settings"))

    suspend fun saveYardSettings(settings: All, firmenId: String = ""): All? {
        val path = if (firmenId.trim().isBlank()) "yard/settings-save" else scopedPath(firmenId, "settings-save")
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$path",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(All.AllNames.serializer(), settings)
            )
        ).await()
        if (!response.ok) return null
        val raw = response.text().await()
        if (raw.trim().isBlank() || raw.trim() == "null") return null
        return decodeOne(raw)
    }

    suspend fun saveKontrolleDefinition(data: All): All? =
        decodeOne(
            call(
                "kontroll-save",
                data.id,
                data.kontrollId,
                data.bezeichnung,
                data.kontrollBezeichnung,
                data.aktivWa.toString(),
                data.aktivWe.toString(),
                data.kontrollTyp,
                data.kontrollOptions.joinToString(","),
                data.stufe.toString(),
                data.abfragePrio.toString(),
                data.kontrollPflicht.toString()
            )
        )

    suspend fun saveKontrolleDefinitionen(data: List<All>): List<All> {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/yard/kontroll-save-batch",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(ListSerializer(All.AllNames.serializer()), data)
            )
        ).await()
        if (!response.ok) error("YardApi kontroll-save-batch failed: ${response.status}")
        return decodeList(response.text().await())
    }

    suspend fun deleteKontrolleDefinition(id: String): Boolean =
        decodeBool(call("kontroll-delete", id))

    suspend fun loadPalettenDefinitionen(firmenId: String = ""): List<All> =
        decodeList(callScoped(firmenId, "palettenarten"))

    suspend fun loadStandorte(firmenId: String = ""): List<All> =
        decodeList(callScoped(firmenId, "standorte"))

    suspend fun savePalettenDefinition(data: All): All? =
        decodeOne(
            call(
                "palettenart-save",
                data.id,
                data.bezeichnung,
                data.aktivWe.toString(),
                data.aktivWa.toString()
            )
        )

    suspend fun deletePalettenDefinition(id: String): Boolean =
        decodeBool(call("palettenart-delete", id))

    suspend fun loadWeListe(firmenId: String = ""): List<All> =
        decodeList(callScoped(firmenId, "we"))

    suspend fun saveWeVorgang(data: All, firmenId: String = ""): All? {
        val saved = decodeOne(callScoped(firmenId, "we-save", serializeAuftrag(data)))
        triggerYardRefresh(saved)
        return saved
    }

    suspend fun loadWeLkwListe(firmenId: String = ""): List<All> =
        decodeList(callScoped(firmenId, "we-lkw"))

    suspend fun loadWeLkwFiltered(filters: All, firmenId: String = ""): List<All> =
        postScopedList(firmenId, "we-lkw-filter", filters)

    suspend fun saveWeLkwVorgang(data: All, firmenId: String = ""): All? {
        val saved = decodeOne(callScoped(firmenId, "we-lkw-save", serializeAuftrag(data)))
        triggerYardRefresh(saved)
        return saved
    }

    suspend fun loadWeVorgangslog(lfdNr: Int, firmenId: String = ""): List<All> =
        decodeList(callScoped(firmenId, "we-vorgangslog", lfdNr.toString()))

    suspend fun appendWeVorgangslog(lfdNr: Int, data: All, firmenId: String = ""): All? {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/${scopedPath(firmenId, "we-vorgangslog-append", lfdNr.toString())}",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(All.AllNames.serializer(), data)
            )
        ).await()
        if (!response.ok) return null
        val saved = decodeOne(response.text().await())
        triggerYardRefresh(saved)
        return saved
    }

    suspend fun loadWeWebKontrollen(firmenId: String = ""): List<All> =
        decodeList(callScoped(firmenId, "we-web-kontrollen"))

    suspend fun loadWeWebKontrolle(auftrag: All, firmenId: String = ""): All? {
        val path = if (firmenId.trim().isBlank()) "yard/we-web-kontrolle" else scopedPath(firmenId, "we-web-kontrolle")
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$path",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(
                    JsonObject.serializer(),
                    buildJsonObject {
                        put("auftrag", json.encodeToJsonElement(All.AllNames.serializer(), auftrag))
                    }
                )
            )
        ).await()
        if (!response.ok) return null
        val raw = response.text().await().trim()
        if (raw.isBlank() || raw == "null") return null
        return decodeOne(raw)
    }

    suspend fun saveWeWebKontrolle(auftrag: All, kontrolle: All, firmenId: String = ""): All? {
        val path = if (firmenId.trim().isBlank()) "yard/we-web-kontrolle-save" else scopedPath(firmenId, "we-web-kontrolle-save")
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$path",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(
                    JsonObject.serializer(),
                    buildJsonObject {
                        put("auftrag", json.encodeToJsonElement(All.AllNames.serializer(), auftrag))
                        put("kontrolle", json.encodeToJsonElement(All.AllNames.serializer(), kontrolle))
                    }
                )
            )
        ).await()
        if (!response.ok) return null
        val saved = decodeOne(response.text().await())
        triggerYardRefresh(saved)
        return saved
    }

    suspend fun loadWaListe(firmenId: String = ""): List<All> =
        decodeList(callScoped(firmenId, "wa"))

    suspend fun saveWaVorgang(data: All, firmenId: String = ""): All? {
        val saved = decodeOne(callScoped(firmenId, "wa-save", serializeAuftrag(data)))
        triggerYardRefresh(saved)
        return saved
    }

    suspend fun loadWaLkwListe(firmenId: String = ""): List<All> =
        decodeList(callScoped(firmenId, "wa-lkw"))

    suspend fun loadWaLkwFiltered(filters: All, firmenId: String = ""): List<All> =
        postScopedList(firmenId, "wa-lkw-filter", filters)

    suspend fun saveWaLkwVorgang(data: All, firmenId: String = ""): All? {
        val saved = decodeOne(callScoped(firmenId, "wa-lkw-save", serializeAuftrag(data)))
        triggerYardRefresh(saved)
        return saved
    }

    suspend fun deleteWaLkwVorgang(tour: String, id: String, firmenId: String = ""): Boolean =
        decodeBool(callScoped(firmenId, "wa-lkw-delete", tour, id))

    suspend fun loadWaVorgangslog(tour: String, firmenId: String = ""): List<All> =
        decodeList(callScoped(firmenId, "wa-vorgangslog", tour))

    suspend fun appendWaVorgangslog(tour: String, data: All, firmenId: String = ""): All? {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/${scopedPath(firmenId, "wa-vorgangslog-append", tour)}",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(All.AllNames.serializer(), data)
            )
        ).await()
        if (!response.ok) return null
        val saved = decodeOne(response.text().await())
        triggerYardRefresh(saved)
        return saved
    }

    suspend fun loadWaStandorteByTour(tour: String, firmenId: String = ""): List<All> =
        decodeList(callScoped(firmenId, "wa-standorte", tour))

    suspend fun loadWaStandorteFiltered(filters: All, firmenId: String = ""): List<All> =
        postScopedList(firmenId, "wa-standorte-filter", filters)

    suspend fun saveWaStandort(tour: String, data: All, firmenId: String = ""): All? {
        val saved = decodeOne(callScoped(firmenId, "wa-standort-save", tour, serializeWaStandort(data)))
        triggerYardRefresh(saved)
        return saved
    }

    suspend fun deleteWaStandort(tour: String, id: String, firmenId: String = ""): Boolean {
        val ok = decodeBool(callScoped(firmenId, "wa-standort-delete", tour, id))
        if (ok) {
            YardLiveSync.markWaStandortDeleted(tour, id, firmenId)
        }
        return ok
    }

    suspend fun loadWeTorfreigaben(firmenId: String = ""): List<All> =
        decodeList(callScoped(firmenId, "we-torfreigaben"))

    suspend fun searchWeTorfreigaben(
        filters: Map<String, String>,
        mandantColumns: Map<String, String>
    ): List<All> =
        decodeList(
            loadWithTabularFilters(
                "yard/we-torfreigaben-tabular",
                filters,
                mandantColumns.mapKeys { (label, _) -> "mandant:$label" }
            )
        )

    suspend fun searchAuftraegeDashboard(
        filters: Map<String, String>, firmenId: String, panel: String, month: String, snap: Boolean
    ): List<All> =
        decodeList(loadWithTabularFilters(
            "auftraegedashboard/search", filters,
            mapOf("firmenId" to firmenId, "panel" to panel, "month" to month, "snap" to snap.toString())
        ))

    suspend fun searchLeitstandHof(
        filters: Map<String, String>,
        view: String,
        standortId: String,
        standortLabel: String,
        selectedType: String = "",
        selectedRef: String = "",
        firmenId: String = ""
    ): List<All> =
        decodeList(loadWithTabularFilters(
            "auftraegedashboard/hof-search", filters,
            mapOf(
                "view" to view, "standortId" to standortId, "standortLabel" to standortLabel,
                "selectedType" to selectedType, "selectedRef" to selectedRef, "firmenId" to firmenId
            )
        ))

    suspend fun searchWaAuskunft(
        filters: Map<String, String>, view: String, tour: String = ""
    ): List<All> =
        decodeList(loadWithTabularFilters(
            "auftraegedashboard/wa-auskunft-search", filters,
            mapOf("view" to view, "tour" to tour)
        ))

    suspend fun searchWeAuskunft(
        filters: Map<String, String>, view: String, ref: String = ""
    ): List<All> =
        decodeList(loadWithTabularFilters(
            "auftraegedashboard/we-auskunft-search", filters,
            mapOf("view" to view, "ref" to ref)
        ))

    suspend fun searchWeWebKontrollen(
        filters: Map<String, String>, view: String, key: String = ""
    ): List<All> =
        decodeList(loadWithTabularFilters(
            "auftraegedashboard/we-web-search", filters,
            mapOf("view" to view, "key" to key)
        ))

    suspend fun searchYardAktivitaetsfeed(
        filters: Map<String, String>, standortId: String, standortLabel: String, firmenId: String = ""
    ): List<All> =
        decodeList(loadWithTabularFilters(
            "auftraegedashboard/aktivitaetsfeed-search", filters,
            mapOf("standortId" to standortId, "standortLabel" to standortLabel, "firmenId" to firmenId)
        ))

    suspend fun saveWeTorfreigabe(data: All, firmenId: String = ""): All? =
        decodeOne(callScoped(firmenId, "we-torfreigabe-save", serializeAuftrag(data)))

    suspend fun loadStandortLayout(standortId: String, firmenId: String = ""): All? {
        val normalizedStandortId = standortId.trim()
        if (normalizedStandortId.isBlank()) return null
        val cacheKey = "${firmenId.trim()}#$normalizedStandortId"
        val loaded = runCatching {
            decodeOne(callScoped(firmenId, "standort-layout", normalizedStandortId))
        }.getOrNull()
        if (loaded != null) standortLayoutCache[cacheKey] = loaded
        return loaded ?: standortLayoutCache[cacheKey]
    }

    suspend fun saveStandortLayout(standortId: String, data: All, firmenId: String = ""): All? {
        val normalizedStandortId = standortId.trim()
        if (normalizedStandortId.isBlank()) return null
        val layoutData = buildJsonObject {
            put("id", normalizedStandortId)
            put("standortZoom", data.standortZoom)
            putJsonObject("standortTiles") {
                data.standortTiles.forEach { (key, value) ->
                    put(key, value)
                }
            }
            putJsonArray("standortTore") {
                data.standortTore.forEach { tor ->
                    add(
                        buildJsonObject {
                            put("id", tor["id"] ?: 0)
                            put("x", tor["x"] ?: 0)
                            put("y", tor["y"] ?: 0)
                        }
                    )
                }
            }
            putJsonArray("standortParkplaetze") {
                data.standortParkplaetze.forEach { parkplatz ->
                    add(
                        buildJsonObject {
                            put("id", parkplatz["id"] ?: 0)
                            put("x", parkplatz["x"] ?: 0)
                            put("y", parkplatz["y"] ?: 0)
                        }
                    )
                }
            }
            putJsonArray("standortOutlines") {
                data.standortOutlines.forEach { outline ->
                    add(buildJsonArray {
                        outline.forEach { cell -> add(JsonPrimitive(cell)) }
                    })
                }
            }
            putJsonArray("standortLabels") {
                data.standortLabels.forEach { label ->
                    add(
                        buildJsonObject {
                            put("id", label["id"].orEmpty())
                            put("name", label["name"].orEmpty())
                            put("type", label["type"].orEmpty())
                            put("reference", label["reference"].orEmpty())
                            put("cells", label["cells"].orEmpty())
                        }
                    )
                }
            }
        }
        val savePath = if (firmenId.isBlank()) {
            "yard/standort-layout-save/${apiPart(normalizedStandortId)}"
        } else {
            "yard/company-standort-layout-save/${apiPart(firmenId.trim())}/${apiPart(normalizedStandortId)}"
        }
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$savePath",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(JsonObject.serializer(), layoutData)
            )
        ).await()
        if (!response.ok) return null
        val saved = decodeOne(response.text().await())
        if (saved != null) {
            standortLayoutCache["${firmenId.trim()}#$normalizedStandortId"] = saved
        }
        return saved
    }

    private suspend fun call(action: String, vararg parts: String): String {
        return LumaticaApiManager.call(
            buildList {
                add("yard")
                add(action)
                parts.forEach { add(it) }
            }.joinToString("/") { apiPart(it) }
        )
    }

    private suspend fun callScoped(firmenId: String, action: String, vararg parts: String): String {
        val normalizedFirmenId = firmenId.trim()
        return if (normalizedFirmenId.isBlank()) {
            call(action, *parts)
        } else {
            LumaticaApiManager.call(scopedPath(normalizedFirmenId, action, *parts))
        }
    }

    private fun scopedPath(firmenId: String, action: String, vararg parts: String): String =
        buildList {
            add("yard")
            add("company-$action")
            add(firmenId)
            parts.forEach { add(it) }
        }.joinToString("/") { apiPart(it) }

    private suspend fun postScopedList(firmenId: String, action: String, data: All): List<All> {
        val path = if (firmenId.trim().isBlank()) {
            "yard/${apiPart(action)}"
        } else {
            scopedPath(firmenId, action)
        }
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$path",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(All.AllNames.serializer(), data)
            )
        ).await()
        if (!response.ok) error("YardApi $path failed: ${response.status}")
        return decodeList(response.text().await())
    }

    private fun serializeAuftrag(data: All): String =
        listOf(
            data.id,
            data.lfdNr.toString(),
            data.datum,
            data.uhrzeitAnmeldung,
            data.uhrzeitTorvergabe,
            data.uhrzeitAbgeschlossen,
            data.torZugewiesenUhrzeit,
            data.lieferscheinnummer,
            data.lieferant,
            data.unternehmer,
            data.auftraggeber,
            data.empfaenger,
            data.ort,
            data.standort,
            data.strasse,
            data.hausnummer,
            data.plz,
            data.firma,
            data.firmenId,
            data.fahrername,
            data.name,
            data.mobil,
            data.lkwkennzeichen,
            data.aufliegerkennzeichen,
            data.auftragsart,
            data.prio,
            data.ablaufPrio.toString(),
            data.verladedatum,
            data.verladeuhrzeit,
            data.platzcode,
            data.platz,
            data.tor?.toString().orEmpty(),
            data.torcode.orEmpty(),
            data.zustand.toString(),
            data.kontrolliert.toString(),
            data.kontrolliertWE.toString(),
            data.belegt.toString(),
            data.aktiv.toString(),
            data.palettenanzahl.toString(),
            data.leerpalettenanzahl.toString(),
            data.palettenWerte.entries.joinToString(";") { "${it.key}=${it.value}" },
            data.paletten.entries.joinToString(";") { "${it.key}=${it.value}" },
            data.tore.joinToString(","),
            data.signatur,
            "",
            data.uhrzeitBis,
            data.waStandortId,
            data.slotStandortId,
            data.slotDriverUserId,
            data.mobileBearbeiterUserId,
            data.mobileBearbeiterName,
            data.mobileBearbeiterKuerzel,
            data.mobileBearbeitungGestartetUm
        ).joinToString("::")

    private fun serializeWaStandort(data: All): String =
        listOf(
            data.id,
            data.lieferscheinnummer,
            data.standort,
            data.ort,
            data.plz,
            data.strasse,
            data.hausnummer,
            data.platzcode,
            data.tor?.toString().orEmpty(),
            data.torcode.orEmpty(),
            data.ablaufPrio.toString(),
            data.kontrolliert.toString(),
            data.uhrzeitAnmeldung,
            data.zustand.toString(),
            data.palettenWerte.entries.joinToString(";") { "${it.key}=${it.value}" },
            "",
            data.verladedatum,
            data.verladeuhrzeit,
            data.slotLhmTypId,
            data.slotLhmTyp,
            data.lhmTypId,
            data.typ,
            data.waStandortId,
            data.slotStandortId,
            data.palettenanzahl.toString()
        ).joinToString("::")

    private fun decodeOne(raw: String): All? {
        val normalized = raw.trim()
        if (normalized.isBlank() || normalized == "null") return null
        return json.decodeFromString(All.AllNames.serializer(), normalized)
    }

    private fun decodeList(raw: String): List<All> {
        if (!isJsonArray(raw)) error("YardApi expected JSON array")
        return json.decodeFromString(ListSerializer(All.AllNames.serializer()), raw)
    }

    private fun decodeBool(raw: String): Boolean =
        raw.contains("\"ok\":true")

    private fun triggerYardRefresh(saved: All?) {
        YardLiveSync.markAuftragChanged(saved)
    }

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(value.ifBlank { EMPTY_FIELD }) as String

    private fun isJsonArray(raw: String): Boolean =
        runCatching {
            val data = js("JSON.parse")(raw)
            val isArray = js("Array.isArray")(data) as Boolean
            isArray
        }.getOrElse { error("YardApi invalid JSON") }
}

